import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Trash2, Download, FileText, CheckCircle, XCircle, 
  Loader2, Package, FolderTree, Eye, FileSpreadsheet,
  Search, Building2, ChevronRight, Filter, List, Grid3X3,
  ArrowUpDown, Calendar, Hash, FileBox, Users, Link2, ArrowRight, Upload, Pencil, Layers, RefreshCw, Edit, Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parse structure code to extract client/family
// Format: TIPO-CLIENTE/FAMILIA-SEQUENCIAL-REVISÃO (e.g., 1-010-12100-00)
// Codes < 100 are Clients, codes >= 100 are Families
const parseStructureCode = (code) => {
  if (!code) return { type: null, client: null, sequential: null, revision: null };
  
  const parts = code.split('-');
  if (parts.length >= 3) {
    return {
      type: parseInt(parts[0]) || null,
      client: parseInt(parts[1]) || null,
      sequential: parts[2] || null,
      revision: parts[3] || null
    };
  }
  return { type: null, client: null, sequential: null, revision: null };
};

// Custom numeric input that handles the "1" deletion issue
const NumericInput = ({ value, onChange, min = 0, placeholder, className, ...props }) => {
  const handleChange = (e) => {
    const newValue = e.target.value;
    // Allow empty string for user to type new value
    if (newValue === '') {
      onChange('');
      return;
    }
    // Only allow numbers and decimal point
    if (/^[0-9]*\.?[0-9]*$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    // On blur, if empty, set to minimum value
    if (value === '' || value === '.') {
      const defaultValue = min > 0 ? min.toString() : '1';
      onChange(defaultValue);
    }
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
};

export default function EstruturasV2() {
  const { canCreate } = useAuth();
  const [estruturas, setEstruturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEstrutura, setSelectedEstrutura] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Dynamic client/family mapping from backend
  const [categoriesMap, setCategoriesMap] = useState({});
  
  // Active tab
  const [activeTab, setActiveTab] = useState('estruturas');
  
  // View mode
  const [viewMode, setViewMode] = useState('clients'); // 'clients' or 'list'
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  
  // Grouped structures by client
  const [groupedEstruturas, setGroupedEstruturas] = useState({});
  
  // Relação (where-used) state
  const [relacaoSearch, setRelacaoSearch] = useState('');
  const [relacaoResults, setRelacaoResults] = useState(null);
  const [searchingRelacao, setSearchingRelacao] = useState(false);
  
  // Relação Completa state (uses ESTRUTURA worksheet - fully exploded)
  const [relacaoCompletaSearch, setRelacaoCompletaSearch] = useState('');
  const [relacaoCompletaResults, setRelacaoCompletaResults] = useState(null);
  const [searchingRelacaoCompleta, setSearchingRelacaoCompleta] = useState(false);
  const [reprocessandoCompletas, setReprocessandoCompletas] = useState(false);
  
  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEstrutura, setNewEstrutura] = useState({
    name: '',
    description: '',
    project: '',
    items: [],
    updateDescriptions: false
  });
  const [newItemCode, setNewItemCode] = useState('');
  const [newItemQty, setNewItemQty] = useState('1');
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [estruturaToDelete, setEstruturaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit items dialog
  const [showEditItemsDialog, setShowEditItemsDialog] = useState(false);
  const [editingItems, setEditingItems] = useState([]);
  const [editItemCode, setEditItemCode] = useState('');
  const [editItemQty, setEditItemQty] = useState('1');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Download state
  const [downloading, setDownloading] = useState(false);
  
  // Excel upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch categories (clients/families) from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/admin/categories/available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Build a mapping: code -> name (for both clients and families)
        const mapping = {};
        
        // Add clients (codes < 100)
        response.data.clients?.forEach(c => {
          mapping[c.code] = c.name;
        });
        
        // Add families (codes >= 100)
        response.data.families?.forEach(f => {
          mapping[f.code] = f.name;
        });
        
        setCategoriesMap(mapping);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Get client/family name from code using dynamic mapping
  const getClientName = useCallback((code) => {
    if (!code) return 'Sem Cliente';
    return categoriesMap[code] || `Cliente ${code}`;
  }, [categoriesMap]);

  useEffect(() => {
    fetchEstruturas();
  }, []);

  // Group structures by client when data changes
  useEffect(() => {
    const grouped = {};
    
    estruturas.forEach(e => {
      // Extract client from project code (e.g., "1-010-12100-00" -> client 010)
      const parsed = parseStructureCode(e.project);
      const clientCode = parsed.client || 0;
      const clientName = getClientName(clientCode);
      
      if (!grouped[clientCode]) {
        grouped[clientCode] = {
          code: clientCode,
          name: clientName,
          structures: []
        };
      }
      grouped[clientCode].structures.push(e);
    });
    
    // Sort structures within each client
    Object.values(grouped).forEach(client => {
      client.structures.sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'items':
            return b.total_items - a.total_items;
          case 'recent':
          default:
            return new Date(b.created_at) - new Date(a.created_at);
        }
      });
    });
    
    setGroupedEstruturas(grouped);
  }, [estruturas, sortBy, getClientName]);

  // Filter structures
  const getFilteredStructures = useCallback(() => {
    let filtered = [...estruturas];
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.name.toLowerCase().includes(term) ||
        (e.project && e.project.toLowerCase().includes(term)) ||
        (e.description && e.description.toLowerCase().includes(term))
      );
    }
    
    // Filter by client
    if (selectedClient !== 'all') {
      filtered = filtered.filter(e => {
        const parsed = parseStructureCode(e.project);
        return parsed.client === parseInt(selectedClient);
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'items':
          return b.total_items - a.total_items;
        case 'recent':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    return filtered;
  }, [estruturas, searchTerm, selectedClient, sortBy]);

  const fetchEstruturas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/estruturas`);
      setEstruturas(response.data || []);
    } catch (error) {
      console.error('Failed to fetch estruturas:', error);
      toast.error('Erro ao carregar estruturas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstruturaDetail = async (estruturaId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/estruturas/${estruturaId}`);
      setSelectedEstrutura(response.data);
    } catch (error) {
      console.error('Failed to fetch estrutura detail:', error);
      toast.error('Erro ao carregar detalhes da estrutura');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!newItemCode.trim()) return;
    
    const qty = parseFloat(newItemQty) || 1;
    setNewEstrutura(prev => ({
      ...prev,
      items: [...prev.items, { code: newItemCode.trim(), quantity: qty }]
    }));
    setNewItemCode('');
    setNewItemQty('1');
  };

  const handleRemoveItem = (index) => {
    setNewEstrutura(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleCreateEstrutura = async () => {
    if (!newEstrutura.name.trim()) {
      toast.error('Nome da estrutura é obrigatório');
      return;
    }
    if (newEstrutura.items.length === 0) {
      toast.error('Adicione pelo menos um item à estrutura');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/estruturas`, {
        name: newEstrutura.name,
        description: newEstrutura.description || null,
        project: newEstrutura.project || null,
        items: newEstrutura.items,
        update_descriptions: newEstrutura.updateDescriptions
      });
      
      // Check if any reserved codes were used
      if (response.data.reserved_codes_used && response.data.reserved_codes_used.length > 0) {
        toast.success(`Estrutura criada! ${response.data.reserved_codes_used.length} código(s) reservado(s) utilizado(s) automaticamente: ${response.data.reserved_codes_used.join(', ')}`, {
          duration: 6000
        });
      } else {
        toast.success('Estrutura criada com sucesso!');
      }
      
      setShowCreateDialog(false);
      setNewEstrutura({ name: '', description: '', project: '', items: [], updateDescriptions: false });
      fetchEstruturas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar estrutura');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEstrutura = async () => {
    if (!estruturaToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/estruturas/${estruturaToDelete.id}`);
      toast.success('Estrutura excluída com sucesso!');
      setShowDeleteDialog(false);
      setEstruturaToDelete(null);
      if (selectedEstrutura?.id === estruturaToDelete.id) {
        setSelectedEstrutura(null);
      }
      fetchEstruturas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir estrutura');
    } finally {
      setDeleting(false);
    }
  };

  const openEditItemsDialog = () => {
    if (!selectedEstrutura) return;
    setEditingItems(
      (selectedEstrutura.items || []).map(it => ({
        code: it.code,
        quantity: it.quantity,
        description: it.description || '',
      }))
    );
    setEditItemCode('');
    setEditItemQty('1');
    setShowEditItemsDialog(true);
  };

  const handleAddEditItem = () => {
    if (!editItemCode.trim()) return;
    const qty = parseFloat(editItemQty) || 1;
    const codeNorm = editItemCode.trim();
    if (editingItems.some(i => (i.code || '').toUpperCase() === codeNorm.toUpperCase())) {
      toast.warning('Este código já está na estrutura');
      return;
    }
    setEditingItems(prev => [...prev, { code: codeNorm, quantity: qty }]);
    setEditItemCode('');
    setEditItemQty('1');
  };

  const handleUpdateEditItemQty = (index, qty) => {
    const parsed = parseFloat(qty) || 0;
    setEditingItems(prev => prev.map((it, i) => (i === index ? { ...it, quantity: parsed } : it)));
  };

  const handleRemoveEditItem = (index) => {
    setEditingItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEditItems = async () => {
    if (!selectedEstrutura) return;
    const cleanItems = editingItems
      .filter(i => i.code && String(i.code).trim())
      .map(i => ({
        code: String(i.code).trim(),
        quantity: parseFloat(i.quantity) > 0 ? parseFloat(i.quantity) : 1,
      }));
    if (cleanItems.length === 0) {
      toast.error('A estrutura precisa de pelo menos um item');
      return;
    }
    setSavingEdit(true);
    try {
      const resp = await axios.put(
        `${API_URL}/api/estruturas/${selectedEstrutura.id}/items`,
        { items: cleanItems }
      );
      setSelectedEstrutura(resp.data);
      setShowEditItemsDialog(false);
      toast.success('Itens da estrutura atualizados!');
      fetchEstruturas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar alterações');
    } finally {
      setSavingEdit(false);
    }
  };



  const handleDownloadDrawings = async (estruturaId) => {
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/estruturas/${estruturaId}/download-drawings`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const estrutura = estruturas.find(e => e.id === estruturaId);
      const fileName = estrutura ? `estrutura_${estrutura.name.replace(/\s+/g, '_')}.zip` : `estrutura_${estruturaId}.zip`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download iniciado!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Nenhum desenho disponível para download');
      } else {
        toast.error('Erro ao baixar desenhos');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/estruturas/parse-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const items = response.data.items || [];
      if (items.length > 0) {
        // Extract name from filename (CÓDIGO - Descrição pattern)
        const fileName = file.name.replace(/\.(xlsx|xls|xlsm)$/i, '');
        const nameParts = fileName.split(' - ');
        const projectCode = nameParts[0]?.trim() || '';
        const projectName = nameParts.length > 1 ? nameParts.slice(1).join(' - ').trim() : fileName;
        
        setNewEstrutura(prev => ({
          ...prev,
          name: projectName || prev.name,
          project: projectCode || prev.project,
          items: items.map(item => ({
            code: item.code,
            quantity: item.quantity || 1,
            description: item.description || ''
          }))
        }));
        toast.success(`${items.length} itens importados do Excel!`);
      } else {
        toast.warning('Nenhum item encontrado no arquivo');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo Excel');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Upload XLSX file for structure
  const handleUploadXlsx = async (estruturaId, file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API_URL}/api/estruturas/${estruturaId}/upload-xlsx`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Arquivo XLSX enviado com sucesso!');
      // Refresh the estrutura detail
      if (selectedEstrutura?.id === estruturaId) {
        fetchEstruturaDetail(estruturaId);
      }
      fetchEstruturas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivo XLSX');
    } finally {
      setUploading(false);
    }
  };

  // Download XLSX file from structure
  const handleDownloadXlsx = async (estruturaId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/api/estruturas/${estruturaId}/download-xlsx`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'estrutura.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download iniciado!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Nenhum arquivo XLSX disponível');
      } else {
        toast.error('Erro ao baixar arquivo');
      }
    }
  };

  // Delete XLSX file from structure
  const handleDeleteXlsx = async (estruturaId) => {
    if (!confirm('Tem certeza que deseja excluir o arquivo XLSX?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/estruturas/${estruturaId}/xlsx`);
      toast.success('Arquivo XLSX excluído!');
      // Refresh
      if (selectedEstrutura?.id === estruturaId) {
        fetchEstruturaDetail(estruturaId);
      }
      fetchEstruturas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir arquivo');
    }
  };

  // Relação (where-used) search
  const handleSearchRelacao = async () => {
    if (!relacaoSearch.trim()) {
      toast.error('Digite um código para buscar');
      return;
    }

    setSearchingRelacao(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/estruturas/relacao/${encodeURIComponent(relacaoSearch.trim())}`
      );
      setRelacaoResults(response.data);
      if (response.data.total_structures === 0) {
        toast.info('Nenhuma estrutura encontrada para este código');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar relações');
      setRelacaoResults(null);
    } finally {
      setSearchingRelacao(false);
    }
  };

  const handleSearchRelacaoCompleta = async () => {
    if (!relacaoCompletaSearch.trim()) {
      toast.error('Digite um código para buscar');
      return;
    }
    setSearchingRelacaoCompleta(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/estruturas/relacao-completa/${encodeURIComponent(relacaoCompletaSearch.trim())}`
      );
      setRelacaoCompletaResults(response.data);
      if (response.data.total_structures === 0) {
        toast.info('Nenhuma estrutura encontrada na lista completa para este código');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar relações');
      setRelacaoCompletaResults(null);
    } finally {
      setSearchingRelacaoCompleta(false);
    }
  };

  const handleReprocessarCompletas = async () => {
    if (!window.confirm('Reprocessar irá baixar todos os XLSX anexados e reindexar a aba "ESTRUTURA". Pode demorar. Continuar?')) return;
    setReprocessandoCompletas(true);
    try {
      const response = await axios.post(`${API_URL}/api/estruturas/reprocessar-completas`);
      const s = response.data;
      toast.success(
        `Reprocessamento concluído: ${s.processed} estrutura(s) indexadas com ${s.total_items_indexed} itens. ` +
        `${s.no_xlsx} sem XLSX, ${s.no_estrutura_sheet} sem aba ESTRUTURA.`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reprocessar');
    } finally {
      setReprocessandoCompletas(false);
    }
  };

  // Get unique clients for filter
  const uniqueClients = Object.values(groupedEstruturas)
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredStructures = getFilteredStructures();

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FolderTree className="w-7 h-7 text-blue-600" />
              Estruturas de Produtos
            </h1>
            <p className="text-slate-500 mt-1">
              Visualize e gerencie estruturas organizadas por cliente
            </p>
          </div>
          
          {canCreate() && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-estrutura-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Estrutura
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{estruturas.length}</p>
                  <p className="text-sm text-slate-500">Total Estruturas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{Object.keys(groupedEstruturas).length}</p>
                  <p className="text-sm text-slate-500">Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Hash className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {estruturas.reduce((acc, e) => acc + (e.total_items || 0), 0)}
                  </p>
                  <p className="text-sm text-slate-500">Total Itens</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileBox className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {estruturas.reduce((acc, e) => acc + (e.items_with_drawings || 0), 0)}
                  </p>
                  <p className="text-sm text-slate-500">Com Desenhos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Estruturas and Relação */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="estruturas" className="flex items-center gap-2">
              <FolderTree className="w-4 h-4" />
              Estruturas
            </TabsTrigger>
            <TabsTrigger value="relacao" className="flex items-center gap-2" data-testid="relacao-tab">
              <Link2 className="w-4 h-4" />
              Relação (Sumarizada)
            </TabsTrigger>
            <TabsTrigger value="relacao-completa" className="flex items-center gap-2" data-testid="relacao-completa-tab">
              <Layers className="w-4 h-4" />
              Relação Lista Completa
            </TabsTrigger>
          </TabsList>

          {/* Estruturas Tab */}
          <TabsContent value="estruturas" className="mt-6 space-y-6">
            {/* Filters and View Toggle */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar estrutura..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Client Filter */}
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filtrar por cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Clientes</SelectItem>
                {uniqueClients.map(client => (
                  <SelectItem key={client.code} value={client.code.toString()}>
                    {client.name} ({client.structures.length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Mais Recentes</SelectItem>
                <SelectItem value="name">Nome (A-Z)</SelectItem>
                <SelectItem value="items">Mais Itens</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'clients' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('clients')}
              className={viewMode === 'clients' ? 'bg-white shadow-sm' : ''}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Por Cliente
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-white shadow-sm' : ''}
            >
              <List className="w-4 h-4 mr-1" />
              Lista
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Structures List */}
          <div className="lg:col-span-1">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  {viewMode === 'clients' ? 'Estruturas por Cliente' : 'Todas as Estruturas'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : viewMode === 'clients' ? (
                  /* Client View */
                  <Accordion type="multiple" className="w-full" defaultValue={Object.keys(groupedEstruturas).slice(0, 3)}>
                    {Object.values(groupedEstruturas)
                      .filter(client => {
                        if (selectedClient !== 'all') {
                          return client.code === parseInt(selectedClient);
                        }
                        if (searchTerm.trim()) {
                          return client.structures.some(e => 
                            e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (e.project && e.project.toLowerCase().includes(searchTerm.toLowerCase()))
                          );
                        }
                        return true;
                      })
                      .sort((a, b) => b.structures.length - a.structures.length)
                      .map(client => (
                        <AccordionItem key={client.code} value={client.code.toString()}>
                          <AccordionTrigger className="px-4 hover:no-underline hover:bg-slate-50">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-blue-600" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-slate-900">{client.name}</p>
                                <p className="text-xs text-slate-500">{client.structures.length} estrutura(s)</p>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-2 pb-2">
                            <div className="space-y-1">
                              {client.structures
                                .filter(e => {
                                  if (!searchTerm.trim()) return true;
                                  return e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    (e.project && e.project.toLowerCase().includes(searchTerm.toLowerCase()));
                                })
                                .map(estrutura => (
                                  <button
                                    key={estrutura.id}
                                    onClick={() => fetchEstruturaDetail(estrutura.id)}
                                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                                      selectedEstrutura?.id === estrutura.id
                                        ? 'bg-blue-50 border border-blue-200'
                                        : 'hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-slate-900 truncate">{estrutura.name}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                          Projeto: {estrutura.project || '-'}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2 ml-2">
                                        <Badge variant="outline" className="text-xs">
                                          {estrutura.total_items} itens
                                        </Badge>
                                        <ChevronRight className="w-4 h-4 text-slate-400" />
                                      </div>
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                      <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        {estrutura.items_with_drawings || 0}
                                      </Badge>
                                      <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        {estrutura.items_without_drawings || 0}
                                      </Badge>
                                    </div>
                                  </button>
                                ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                  </Accordion>
                ) : (
                  /* List View */
                  <ScrollArea className="h-[600px]">
                    <div className="p-2 space-y-1">
                      {filteredStructures.map(estrutura => (
                        <button
                          key={estrutura.id}
                          onClick={() => fetchEstruturaDetail(estrutura.id)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            selectedEstrutura?.id === estrutura.id
                              ? 'bg-blue-50 border border-blue-200'
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate">{estrutura.name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {estrutura.project || '-'} • {getClientName(parseStructureCode(estrutura.project).client)}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {estrutura.total_items} itens
                            </Badge>
                            <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100">
                              {estrutura.items_with_drawings || 0} c/ desenho
                            </Badge>
                          </div>
                        </button>
                      ))}
                      {filteredStructures.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p>Nenhuma estrutura encontrada</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200 shadow-sm h-full">
              {detailLoading ? (
                <div className="flex items-center justify-center h-96">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : selectedEstrutura ? (
                <>
                  <CardHeader className="pb-4 border-b">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{selectedEstrutura.name}</CardTitle>
                        <p className="text-sm text-slate-500 mt-1">
                          Projeto: <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{selectedEstrutura.project || '-'}</span>
                        </p>
                        {selectedEstrutura.description && (
                          <p className="text-sm text-slate-600 mt-2">{selectedEstrutura.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDrawings(selectedEstrutura.id)}
                          disabled={downloading || !selectedEstrutura.items_with_drawings}
                        >
                          {downloading ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-1" />
                          )}
                          Baixar Desenhos ({selectedEstrutura.items_with_drawings})
                        </Button>
                        {canCreate() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={openEditItemsDialog}
                            data-testid="edit-items-btn"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar Itens
                          </Button>
                        )}
                        {canCreate() && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setEstruturaToDelete(selectedEstrutura);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex gap-4 mt-4">
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                        <Hash className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium">{selectedEstrutura.total_items} itens</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">{selectedEstrutura.items_with_drawings} com desenhos</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-700">{selectedEstrutura.items_without_drawings} sem desenhos</span>
                      </div>
                    </div>

                    {/* XLSX File Section */}
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileSpreadsheet className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-sm">Arquivo de Estrutura Completa</p>
                            {selectedEstrutura.xlsx_file ? (
                              <p className="text-xs text-slate-500">
                                {selectedEstrutura.xlsx_file.filename} • 
                                {(selectedEstrutura.xlsx_file.file_size / 1024).toFixed(1)} KB • 
                                Enviado por {selectedEstrutura.xlsx_file.uploaded_by_name}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500">Nenhum arquivo anexado</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {selectedEstrutura.xlsx_file ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadXlsx(selectedEstrutura.id, selectedEstrutura.xlsx_file.filename)}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Baixar XLSX
                              </Button>
                              {canCreate() && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteXlsx(selectedEstrutura.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          ) : canCreate() && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadXlsx(selectedEstrutura.id, file);
                                  e.target.value = '';
                                }}
                              />
                              <Button variant="outline" size="sm" asChild disabled={uploading}>
                                <span>
                                  {uploading ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-1" />
                                  )}
                                  Anexar XLSX
                                </span>
                              </Button>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-0">
                    <ScrollArea className="h-[500px]">
                      <Table>
                        <TableHeader className="sticky top-0 bg-white">
                          <TableRow>
                            <TableHead className="w-40">Código</TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead className="w-20 text-center">Qtd</TableHead>
                            <TableHead className="w-32 text-center">Desenhos</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedEstrutura.items?.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-mono text-sm">{item.code}</TableCell>
                              <TableCell className="text-sm text-slate-600">
                                {item.description || '-'}
                              </TableCell>
                              <TableCell className="text-center">{item.quantity}</TableCell>
                              <TableCell className="text-center">
                                {item.available_drawings?.length > 0 ? (
                                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                    {item.available_drawings.length} arquivo(s)
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-400">
                                    Ausente
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-96 text-slate-400">
                  <Eye className="w-12 h-12 mb-4" />
                  <p className="text-lg font-medium">Selecione uma estrutura</p>
                  <p className="text-sm">Clique em uma estrutura para ver os detalhes</p>
                </div>
              )}
            </Card>
          </div>
        </div>
          </TabsContent>

          {/* Relação Tab */}
          <TabsContent value="relacao" className="mt-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-blue-500" />
                  Onde o Item é Utilizado
                </CardTitle>
                <CardDescription>
                  Digite um código de material para ver em quais estruturas ele é consumido
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search Input */}
                <div className="flex gap-3">
                  <Input
                    placeholder="Digite o código do material (ex: 6-127-13671)"
                    value={relacaoSearch}
                    onChange={(e) => setRelacaoSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchRelacao()}
                    className="flex-1"
                    data-testid="relacao-search-input"
                  />
                  <Button
                    onClick={handleSearchRelacao}
                    disabled={searchingRelacao || !relacaoSearch.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="relacao-search-btn"
                  >
                    {searchingRelacao ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Buscar
                  </Button>
                </div>

                {/* Results */}
                {relacaoResults && (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Código pesquisado</p>
                          <code className="text-lg font-semibold text-slate-900 bg-white px-3 py-1 rounded border">
                            {relacaoResults.code}
                          </code>
                          {relacaoResults.base_code !== relacaoResults.code && (
                            <span className="ml-2 text-sm text-slate-500">
                              (base: {relacaoResults.base_code})
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Encontrado em</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {relacaoResults.total_structures}
                            <span className="text-base font-normal text-slate-500 ml-1">
                              estrutura{relacaoResults.total_structures !== 1 ? 's' : ''}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Structures List */}
                    {relacaoResults.total_structures > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead>Estrutura</TableHead>
                              <TableHead>Projeto</TableHead>
                              <TableHead className="text-center w-[100px]">Qtd. Total</TableHead>
                              <TableHead className="text-center w-[100px]">Itens</TableHead>
                              <TableHead className="w-[150px]">Criado em</TableHead>
                              <TableHead className="w-[80px]"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {relacaoResults.structures.map((struct) => (
                              <TableRow key={struct.id} className="hover:bg-slate-50">
                                <TableCell>
                                  <div className="font-medium text-slate-900">{struct.name}</div>
                                  {struct.description && (
                                    <div className="text-xs text-slate-500 truncate max-w-[200px]">
                                      {struct.description}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {struct.project ? (
                                    <Badge variant="outline">{struct.project}</Badge>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                                    {struct.total_quantity}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center text-sm text-slate-500">
                                  {struct.total_items}
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">
                                  {new Date(struct.created_at).toLocaleDateString('pt-BR')}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setActiveTab('estruturas');
                                      fetchEstruturaDetail(struct.id);
                                    }}
                                    className="h-8 text-blue-600 hover:text-blue-800"
                                    title="Ver estrutura"
                                  >
                                    <ArrowRight className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-lg">
                        <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Este código não está sendo utilizado em nenhuma estrutura</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Empty State */}
                {!relacaoResults && (
                  <div className="text-center py-12 text-slate-400">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Digite um código de material para buscar em quais estruturas ele é utilizado</p>
                    <p className="text-sm mt-1">Exemplo: 6-127-13671 ou 8-130-16091-00</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Relação Completa Tab - reads from ESTRUTURA worksheet */}
          <TabsContent value="relacao-completa" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Layers className="w-5 h-5 text-purple-600" />
                      Relação por Lista Completa
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Busca no arquivo Excel de estrutura, aba <strong>ESTRUTURA</strong> (lista explodida, com todos os subníveis). As quantidades são somadas quando o mesmo código aparece em várias linhas.
                    </CardDescription>
                  </div>
                  {canCreate() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReprocessarCompletas}
                      disabled={reprocessandoCompletas}
                      data-testid="btn-reprocessar-completas"
                      title="Reprocessar todos os XLSX já anexados"
                    >
                      {reprocessandoCompletas ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Reprocessar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Digite o código (ex: 8-010-60137 ou 8-010-60137-Z0)"
                    value={relacaoCompletaSearch}
                    onChange={(e) => setRelacaoCompletaSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchRelacaoCompleta()}
                    className="flex-1"
                    data-testid="input-search-relacao-completa"
                  />
                  <Button
                    onClick={handleSearchRelacaoCompleta}
                    disabled={searchingRelacaoCompleta}
                    data-testid="btn-search-relacao-completa"
                  >
                    {searchingRelacaoCompleta ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Buscar
                  </Button>
                </div>

                {relacaoCompletaResults && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 flex-wrap p-3 bg-purple-50 rounded-lg">
                      <span className="font-mono font-semibold text-purple-900">
                        {relacaoCompletaResults.code}
                      </span>
                      {relacaoCompletaResults.base_code !== relacaoCompletaResults.code && (
                        <span className="text-xs text-slate-500">
                          (base: {relacaoCompletaResults.base_code})
                        </span>
                      )}
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Badge className="bg-purple-100 text-purple-700">
                        {relacaoCompletaResults.total_structures} estrutura
                        {relacaoCompletaResults.total_structures !== 1 ? 's' : ''}
                      </Badge>
                    </div>

                    {relacaoCompletaResults.total_structures > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Estrutura</TableHead>
                            <TableHead className="text-right">Total de Itens</TableHead>
                            <TableHead>Códigos encontrados</TableHead>
                            <TableHead className="text-right">Qtd Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {relacaoCompletaResults.structures.map((struct) => (
                            <TableRow key={struct.estrutura_id}>
                              <TableCell className="font-medium">
                                <button
                                  className="text-blue-600 hover:underline text-left"
                                  onClick={() => {
                                    setActiveTab('estruturas');
                                    fetchEstruturaDetail(struct.estrutura_id);
                                  }}
                                  data-testid={`btn-view-completa-${struct.estrutura_id}`}
                                >
                                  {struct.estrutura_name}
                                </button>
                              </TableCell>
                              <TableCell className="text-right text-slate-500">
                                {struct.total_items_estrutura}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  {struct.matching_items.map((item, idx) => (
                                    <div key={idx} className="text-xs flex items-center gap-2">
                                      <span className="font-mono">{item.code}</span>
                                      <span className="text-slate-400">×</span>
                                      <span>{item.quantity}</span>
                                      {item.unit && (
                                        <span className="text-slate-500">{item.unit}</span>
                                      )}
                                      {item.description && (
                                        <span className="text-slate-500 italic truncate max-w-xs">
                                          — {item.description}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {struct.total_quantity}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        Nenhuma estrutura contém o código{' '}
                        <span className="font-mono">{relacaoCompletaResults.code}</span> na lista completa.
                      </div>
                    )}
                  </div>
                )}

                {!relacaoCompletaResults && (
                  <div className="text-center py-12 text-slate-400">
                    <Layers className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Digite um código para buscar na lista completa (aba <strong>ESTRUTURA</strong>)</p>
                    <p className="text-xs mt-2">
                      Diferente da Relação Sumarizada, aqui buscamos na explosão total dos subníveis com quantidades acumuladas.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Nova Estrutura</DialogTitle>
              <DialogDescription>
                Crie uma nova estrutura de produto. Você pode adicionar itens manualmente ou importar de um arquivo Excel.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Excel Upload */}
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <FileSpreadsheet className="w-8 h-8 text-blue-600" />
                <div className="flex-1">
                  <p className="font-medium text-blue-900">Importar do Excel</p>
                  <p className="text-sm text-blue-700">O nome do arquivo será usado como nome do projeto (CÓDIGO - Descrição)</p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls,.xlsm"
                  onChange={handleExcelUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-blue-200 text-blue-700 hover:bg-blue-100"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  Selecionar Arquivo
                </Button>
              </div>
              
              {/* Form Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome da Estrutura *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: COFRE PROTEGE ONE"
                    value={newEstrutura.name}
                    onChange={(e) => setNewEstrutura(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project">Código do Projeto</Label>
                  <Input
                    id="project"
                    placeholder="Ex: 1-010-12100-00"
                    value={newEstrutura.project}
                    onChange={(e) => setNewEstrutura(prev => ({ ...prev, project: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Descrição opcional da estrutura"
                  value={newEstrutura.description}
                  onChange={(e) => setNewEstrutura(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              
              {/* Add Item */}
              <div className="space-y-2">
                <Label>Adicionar Item</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Código do item"
                    value={newItemCode}
                    onChange={(e) => setNewItemCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                    className="flex-1"
                  />
                  <NumericInput
                    placeholder="Qtd"
                    value={newItemQty}
                    onChange={setNewItemQty}
                    min={1}
                    className="w-24"
                  />
                  <Button onClick={handleAddItem} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Items List */}
              {newEstrutura.items.length > 0 && (
                <div className="border rounded-lg">
                  <div className="p-2 bg-slate-50 border-b flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700">
                      {newEstrutura.items.length} item(s) adicionado(s)
                    </p>
                    {newEstrutura.items.some(item => item.code?.startsWith('8')) && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        Códigos iniciados em 8 podem ser renomeados
                      </span>
                    )}
                  </div>
                  <ScrollArea className="h-48">
                    <div className="p-2 space-y-1">
                      {newEstrutura.items.map((item, index) => {
                        const canRename = item.code?.startsWith('8');
                        return (
                          <div
                            key={index}
                            className={`flex items-center justify-between p-2 rounded ${canRename ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {canRename ? (
                                <Input
                                  value={item.code}
                                  onChange={(e) => {
                                    const updatedItems = [...newEstrutura.items];
                                    updatedItems[index] = { ...updatedItems[index], code: e.target.value };
                                    setNewEstrutura(prev => ({ ...prev, items: updatedItems }));
                                  }}
                                  className="font-mono text-sm w-40 h-8 bg-white"
                                  title="Código editável (começa com 8)"
                                />
                              ) : (
                                <span className="font-mono text-sm">{item.code}</span>
                              )}
                              {item.description && (
                                <span className="text-sm text-slate-500 truncate max-w-xs">
                                  {item.description}
                                </span>
                              )}
                              {canRename && (
                                <Pencil className="w-3 h-3 text-amber-500" />
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Qtd: {item.quantity}</Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}
              
              {/* Update Descriptions Option */}
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                <Checkbox
                  id="updateDescriptions"
                  checked={newEstrutura.updateDescriptions}
                  onCheckedChange={(checked) => 
                    setNewEstrutura(prev => ({ ...prev, updateDescriptions: checked }))
                  }
                />
                <Label htmlFor="updateDescriptions" className="text-sm text-amber-800 cursor-pointer">
                  Atualizar descrições no catálogo (se o Excel contiver descrições)
                </Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateEstrutura} 
                disabled={creating || !newEstrutura.name.trim() || newEstrutura.items.length === 0}
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Criar Estrutura
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Estrutura</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir a estrutura &quot;{estruturaToDelete?.name}&quot;?
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteEstrutura}
                className="bg-red-600 hover:bg-red-700"
                disabled={deleting}
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Items Dialog */}
        <Dialog open={showEditItemsDialog} onOpenChange={setShowEditItemsDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="edit-items-dialog">
            <DialogHeader>
              <DialogTitle>Editar Itens - {selectedEstrutura?.name}</DialogTitle>
              <DialogDescription>
                Adicione, remova ou altere a quantidade dos itens desta estrutura.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-end gap-2 py-3 border-b">
              <div className="flex-1">
                <Label className="text-xs">Código do Item</Label>
                <Input
                  value={editItemCode}
                  onChange={(e) => setEditItemCode(e.target.value)}
                  placeholder="Ex: 4-107-00095"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddEditItem()}
                  data-testid="edit-item-code-input"
                />
              </div>
              <div className="w-24">
                <Label className="text-xs">Qtd</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editItemQty}
                  onChange={(e) => setEditItemQty(e.target.value)}
                  data-testid="edit-item-qty-input"
                />
              </div>
              <Button onClick={handleAddEditItem} data-testid="edit-item-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              <div className="text-xs text-slate-500 mb-2">
                {editingItems.length} item(s) na estrutura
              </div>
              {editingItems.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1.5 border-b last:border-b-0" data-testid={`edit-item-row-${idx}`}>
                  <div className="flex-1 font-mono text-sm">{it.code}</div>
                  <div className="text-xs text-slate-500 flex-1 truncate">{it.description || ''}</div>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="w-20 h-8"
                    value={it.quantity}
                    onChange={(e) => handleUpdateEditItemQty(idx, e.target.value)}
                    data-testid={`edit-item-qty-${idx}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleRemoveEditItem(idx)}
                    data-testid={`edit-item-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {editingItems.length === 0 && (
                <div className="text-center text-slate-400 py-8">Nenhum item</div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditItemsDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveEditItems} disabled={savingEdit} data-testid="edit-items-save-btn">
                {savingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


      </div>
    </Layout>
  );
}
